import { useEffect } from 'react';
import type { RefObject } from 'react';
import type { WorkbookInstance } from '@fortune-sheet/react';
import { MIN_ROW_HEIGHT } from '../fileTypes';
import { getInternals, isRectVisible, FILTER_MENU_SELECTOR } from './workbookInternals';
import { SCROLLBAR_X_SELECTOR } from './selectors';

const SHEET_CONTAINER_SELECTOR = '.fortune-sheet-container';
const SCROLLBAR_Y_SELECTOR = '.luckysheet-scrollbar-y';

/** Pure delta normaliser. `deltaMode` 0 is already pixels. 1 is "lines"
 *  (one row, `MIN_ROW_HEIGHT` px). 2 is "pages" (the scrollbar's own client
 *  size). A Shift-modified vertical-only gesture becomes horizontal, matching
 *  the common trackpad/mouse convention. */
export const normaliseWheel = (
    e: { deltaX: number; deltaY: number; deltaMode: number; shiftKey?: boolean },
    rowPx: number,
    pageX: number,
    pageY: number,
): { dx: number; dy: number } => {
    let dx = e.deltaX;
    let dy = e.deltaY;
    if (e.deltaMode === 1) {
        dx *= rowPx;
        dy *= rowPx;
    } else if (e.deltaMode === 2) {
        dx *= pageX;
        dy *= pageY;
    }
    if (e.shiftKey && dx === 0 && dy !== 0) {
        dx = dy;
        dy = 0;
    }
    return { dx, dy };
};

export interface UseSheetWheelArgs {
    wrapperRef: RefObject<HTMLElement>;
    workbookRef: RefObject<WorkbookInstance>;
    workbookMounted: boolean;
}

/**
 * Capture-phase wheel listener that drives the native
 * scrollbars directly, bypassing the library's own `handleGlobalWheel` (1 row
 * per notch, fixed 20px horizontal). `stopPropagation` keeps the event from
 * ever reaching the library's bubble-phase wheel listener on
 * `.fortune-sheet-container`; the scrollbar's own `onScroll` (already wired by
 * the library) then syncs context and redraws — no library patch needed.
 *
 * Attached in an effect keyed on `workbookMounted` (no MutationObserver
 * re-attach) — unlike the sheet-tab strip, the wrapper div itself is a stable
 * React-owned node, and the scrollbar elements are looked up fresh on every
 * wheel event rather than cached at attach time.
 */
export const useSheetWheel = ({ wrapperRef, workbookRef, workbookMounted }: UseSheetWheelArgs): void => {
    useEffect(() => {
        if (!workbookMounted) return undefined;
        const wrapper = wrapperRef.current;
        if (!wrapper) return undefined;

        const onWheel = (e: WheelEvent) => {
            if (e.ctrlKey || e.metaKey) {
                // Browser/OS zoom — never handled, and never preventDefault'd.
                // Still stopPropagation: the library's own bubble-phase wheel
                // listener (`@fortune-sheet/react`'s Workbook) preventDefaults
                // UNCONDITIONALLY, which would block the browser's zoom right
                // back out from under this early return.
                e.stopPropagation();
                return;
            }
            if (!(e.target instanceof Element) || !e.target.closest(SHEET_CONTAINER_SELECTOR)) return;

            // Queried once and reused below — a live filter menu blocks the
            // wheel (modal, like Excel); a stale one still needs clearing.
            const filterMenu = wrapper.querySelector(FILTER_MENU_SELECTOR);
            if (filterMenu && isRectVisible(filterMenu.getBoundingClientRect())) return;

            const scrollbarY = wrapper.querySelector<HTMLElement>(SCROLLBAR_Y_SELECTOR);
            const scrollbarX = wrapper.querySelector<HTMLElement>(SCROLLBAR_X_SELECTOR);
            if (!scrollbarY && !scrollbarX) return;

            // clientWidth/clientHeight are only meaningful for deltaMode 2
            // (page scroll) — skip the layout read otherwise.
            const pageX = e.deltaMode === 2 ? scrollbarX?.clientWidth ?? 0 : 0;
            const pageY = e.deltaMode === 2 ? scrollbarY?.clientHeight ?? 0 : 0;
            const { dx, dy } = normaliseWheel(e, MIN_ROW_HEIGHT, pageX, pageY);
            if (scrollbarY) scrollbarY.scrollTop += dy;
            if (scrollbarX) scrollbarX.scrollLeft += dx;

            // A stale/off-screen filter menu also revives the keyboard
            // (core:77151 gates on it), so clear it whenever we scroll past it.
            if (filterMenu) {
                const internals = getInternals(workbookRef.current);
                if (internals) {
                    internals.setContextWithProduce((draft) => {
                        draft.filterContextMenu = undefined;
                    });
                }
            }

            e.preventDefault();
            e.stopPropagation();
        };

        wrapper.addEventListener('wheel', onWheel, { capture: true, passive: false });
        return () => wrapper.removeEventListener('wheel', onWheel, { capture: true });
    }, [wrapperRef, workbookRef, workbookMounted]);
};
